import React, { useEffect, useState, useMemo } from 'react';
import { getStylingSync } from '../api/api';

/* Chevron als SVG — 14px, past bij admin-UI. */
function Chevron({ open, size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        flexShrink: 0,
        transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
        transition: 'transform 0.15s ease',
      }}
      aria-hidden="true"
    >
      <polyline points="6 4 11 8 6 12" />
    </svg>
  );
}

const KOLOM_DEFINITIES = [
  {
    titel: 'Kwetsbare gebieden',
    categorie: 'kwetsbaar',
    extra_categorie: 'polder',  // polders onderaan dezelfde kolom
    extra_titel: 'Polders',
  },
  {
    titel: 'Waterkeringen',
    categorie: 'waterstaatswerk_kering',
  },
  {
    titel: 'Oppervlaktewater',
    categorie: 'waterstaatswerk_water',
    plat: true,  // geen sub-groepering — toon types los
  },
];

const SUB_LABELS = {
  primaire_kering: 'Primaire kering',
  regionale_kering: 'Regionale kering',
  compartimenteringskering: 'Compartimenteringskering',
  overige_kering: 'Overige kering',
  boezem_kering: 'Boezemkade',
  a_watergang: 'A-watergang',
  b_watergang: 'B-watergang',
};

/* HEX (#rrggbb) + opacity → rgba string. Voor swatch. */
function hexNaarRgba(hex, opacity) {
  if (!hex || hex.length < 7) return `rgba(0,0,0,${opacity})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/* Swatch (16x16): rechthoekje met fill + border, identiek aan beheertools-preview maar kleiner. */
function Swatch({ row }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 16,
        height: 16,
        background: hexNaarRgba(row.kleur_fill, row.fill_opacity),
        border: `1.5px solid ${row.kleur_stroke}`,
        borderRadius: 3,
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    />
  );
}

/* Lege rij — checkbox + swatch + label. Voor de niet-gegroepeerde types. */
function LaagRij({ row, zichtbaar, onToggle }) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 0',
        cursor: 'pointer',
        fontSize: 13,
        userSelect: 'none',
      }}
    >
      <input
        type="checkbox"
        checked={zichtbaar}
        onChange={onToggle}
        style={{ margin: 0, cursor: 'pointer' }}
      />
      <Swatch row={row} />
      <span>{row.label_kort || row.label}</span>
    </label>
  );
}

/* Sub-groep met master-checkbox + chevron. Klik op tekst/checkbox = toggle alle, klik op chevron = expand. */
function SubGroep({ subKey, rijen, zichtbaarPerType, setZichtbaar }) {
  const [open, setOpen] = useState(false);  // dichtgeklapt by default
  const types = rijen.map(r => r.type);
  const aantalAan = types.filter(t => zichtbaarPerType[t]).length;
  const allesAan = aantalAan === types.length;
  const niksAan = aantalAan === 0;

  const masterToggle = () => {
    const nieuw = { ...zichtbaarPerType };
    const doelwaarde = !allesAan;  // als alles aan: zet alles uit. Anders: zet alles aan.
    types.forEach(t => { nieuw[t] = doelwaarde; });
    setZichtbaar(nieuw);
  };

  const togglePijl = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(o => !o);
  };

  return (
    <div style={{ marginBottom: 2 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 0',
          fontSize: 13,
          userSelect: 'none',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={allesAan}
            ref={el => { if (el) el.indeterminate = !allesAan && !niksAan; }}
            onChange={masterToggle}
            style={{ margin: 0, cursor: 'pointer' }}
          />
          <span>{SUB_LABELS[subKey] || subKey} ({rijen.length})</span>
        </label>
        <button
          onClick={togglePijl}
          aria-label={open ? 'Inklappen' : 'Uitklappen'}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 4,
            cursor: 'pointer',
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Chevron open={open} />
        </button>
      </div>
      {open && (
        <div style={{ paddingLeft: 22 }}>
          {rijen.map(r => (
            <LaagRij
              key={r.type}
              row={r}
              zichtbaar={!!zichtbaarPerType[r.type]}
              onToggle={() => setZichtbaar({ ...zichtbaarPerType, [r.type]: !zichtbaarPerType[r.type] })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* Kolom: een cluster categorieën met een titel.
   Toont losse types als LaagRij; subgroepen via SubGroep. */
function Kolom({ titel, rijen, zichtbaarPerType, setZichtbaar, plat = false }) {
  // Splits rijen in losse types (subcategorie === null) en gegroepeerde.
  // Bij 'plat'-modus: alle types als losse rijen, geen sub-groepen.
  const losse = plat ? rijen : rijen.filter(r => !r.subcategorie);
  const gegroepeerd = {};
  if (!plat) {
    rijen.filter(r => r.subcategorie).forEach(r => {
      if (!gegroepeerd[r.subcategorie]) gegroepeerd[r.subcategorie] = [];
      gegroepeerd[r.subcategorie].push(r);
    });
  }

  return (
    <div style={{ flex: '1 1 200px', minWidth: 200 }}>
      <h4 style={{
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--text-muted)',
        margin: '0 0 6px 0',
      }}>{titel}</h4>
      {losse.map(r => (
        <LaagRij
          key={r.type}
          row={r}
          zichtbaar={!!zichtbaarPerType[r.type]}
          onToggle={() => setZichtbaar({ ...zichtbaarPerType, [r.type]: !zichtbaarPerType[r.type] })}
        />
      ))}
      {Object.keys(gegroepeerd).sort().map(sub => (
        <SubGroep
          key={sub}
          subKey={sub}
          rijen={gegroepeerd[sub]}
          zichtbaarPerType={zichtbaarPerType}
          setZichtbaar={setZichtbaar}
        />
      ))}
    </div>
  );
}

/* Puntsoorten in de legenda. De kleuren volgen de taal van het
   meldingsformulier: blauw is een put die er al is, oranje een put die nog
   geboord moet worden. Gevuld tegenover hol onderscheidt wel/niet in het
   register — dat blijft zichtbaar voor wie kleuren slecht uit elkaar houdt. */
const PUNT_KLEUR = {
  gekoppeld: '#0071e3',
  ongeregistreerd: '#0071e3',
  nieuw: '#ff9500',
  onbeslist: '#b25000',
  overig: '#c7c7cc',
};

function PuntStip({ soort }) {
  const kleur = PUNT_KLEUR[soort] || '#86868b';
  const hol = soort === 'ongeregistreerd' || soort === 'onbeslist';
  return (
    <span style={{
      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
      background: hol ? '#fff' : kleur,
      boxShadow: hol ? `inset 0 0 0 2.5px ${kleur}` : 'none',
      flexShrink: 0,
    }} />
  );
}

/**
 * KaartLegenda — collapse-paneel onder de kaart met laag-toggles.
 * Default dicht (alleen "Legenda"-titel zichtbaar). Klik op titel = open/dicht.
 *
 * Props:
 *   onZichtbaarheidChange(map: { type → boolean }) — callback elke keer dat
 *     een toggle wijzigt. RDKaart kan hierop filteren welke overlays getoond worden.
 *   puntLegenda: [{ soort, label, aantal? }] — soorten markers op de kaart.
 *     Staat altijd open en verschijnt ook als er geen gebiedstyling geladen is.
 *   zetAan: { types: string[], nonce: number } — zet deze lagen aan, ook als ze
 *     uitstonden. Wijs je op de kaart een plek aan die in een waterstaatswerk
 *     ligt, dan hoor je dat werk te zien; stond de laag uit, dan zag je alleen
 *     een label zonder vlak. De nonce laat dezelfde set opnieuw doorwerken.
 */
export default function KaartLegenda({ onZichtbaarheidChange, puntLegenda = [], zetAan = null }) {
  const [open, setOpen] = useState(false);  // collapse-paneel: dicht by default
  const [zichtbaarPerType, setZichtbaarInternal] = useState({});

  const styling = getStylingSync();

  // Bij eerste render: initialiseer toggle-state op basis van standaard_zichtbaar
  useEffect(() => {
    if (!styling) return;
    const init = {};
    styling.forEach(r => { init[r.type] = !!r.standaard_zichtbaar; });
    setZichtbaarInternal(init);
    if (onZichtbaarheidChange) onZichtbaarheidChange(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styling]);

  const setZichtbaar = (nieuw) => {
    setZichtbaarInternal(nieuw);
    if (onZichtbaarheidChange) onZichtbaarheidChange(nieuw);
  };

  /* Lagen die van buitenaf aangezet worden. Alleen aan, nooit uit: wat de
     gebruiker zelf heeft aangezet blijft staan. */
  const nonce = zetAan?.nonce;
  useEffect(() => {
    const types = zetAan?.types || [];
    if (!types.length) return;
    if (types.every((t) => zichtbaarPerType[t])) return;
    const nieuw = { ...zichtbaarPerType };
    types.forEach((t) => { nieuw[t] = true; });
    setZichtbaar(nieuw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  // Per kolom de relevante rijen verzamelen
  const kolommen = useMemo(() => {
    if (!styling) return [];
    return KOLOM_DEFINITIES.map(def => {
      const hoofdrijen = styling
        .filter(r => r.categorie === def.categorie)
        .sort((a, b) => a.volgorde - b.volgorde);
      const extra = def.extra_categorie
        ? styling.filter(r => r.categorie === def.extra_categorie).sort((a, b) => a.volgorde - b.volgorde)
        : [];
      return { ...def, rijen: hoofdrijen, extra };
    });
  }, [styling]);

  const punten = (
    puntLegenda.length > 0 && (
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '6px 20px',
        alignItems: 'center', padding: '10px 0 2px',
      }}>
        {puntLegenda.map(p => (
          <span key={p.soort} style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            fontSize: 13, color: 'var(--text-soft)',
          }}>
            <PuntStip soort={p.soort} />
            {p.label}
            {p.aantal != null && (
              <span style={{
                fontSize: 12, fontWeight: 600, color: 'var(--text-soft)',
                background: 'var(--surface-3)', borderRadius: 20, padding: '0 7px',
              }}>{p.aantal}</span>
            )}
          </span>
        ))}
      </div>
    )
  );

  // Geen gebiedstyling geladen? Dan alleen de puntsoorten, geen uitklapper.
  if (!styling) return punten || null;

  return (
    <div
      style={{
        marginTop: 8,
      }}
    >
      {punten}
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px 10px 0',
          background: 'transparent',
          border: 'none',
          fontSize: 15,
          fontWeight: 600,
          cursor: 'pointer',
          color: 'var(--text)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center' }}>Legenda</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', transform: 'translateY(1px)' }}>
          <Chevron open={open} size={13} />
        </span>
      </button>
      {open && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 24,
            padding: '4px 14px 14px 0',
          }}
        >
          {kolommen.map(kol => (
            <div key={kol.titel} style={{ flex: '1 1 200px', minWidth: 200 }}>
              <Kolom
                titel={kol.titel}
                rijen={kol.rijen}
                zichtbaarPerType={zichtbaarPerType}
                setZichtbaar={setZichtbaar}
                plat={kol.plat}
              />
              {kol.extra && kol.extra.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Kolom
                    titel={kol.extra_titel}
                    rijen={kol.extra}
                    zichtbaarPerType={zichtbaarPerType}
                    setZichtbaar={setZichtbaar}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
